import React, { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import StorageIcon from '@mui/icons-material/Storage';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/authContext';
import useFrontendUsage from '../../hooks/useFrontendUsage';
import { buildNewFrontendUrl, PUBLIC_PATHS } from '../../util/frontendMigration';
import { resolveViewTitle } from '../../util/viewTitle';

const INTRODUCTION_KEY = 'newManagementFrontend';

// The dialog leads into the new frontend: switching is the only offered action
// at first. The way out appears after this delay, quietly, as a text link.
const DISMISS_REVEAL_MS = 6000;

const HIGHLIGHTS = [
  {
    Icon: StorageIcon,
    title: 'Gleiche Daten, gleicher Login',
    description:
      'Beide Oberflächen arbeiten auf demselben System. Nichts geht verloren, du landest auf derselben Ansicht.',
  },
  {
    Icon: SwapHorizIcon,
    title: 'Zurück geht immer',
    description:
      'Über das Funkeln-Symbol oben rechts kommst du jederzeit wieder hierher – ausprobieren kostet nichts.',
  },
  {
    Icon: FavoriteBorderIcon,
    title: 'Dein Feedback zählt',
    description:
      'Sag uns direkt im neuen Tool, was dir fehlt – so machen wir es gemeinsam fertig.',
  },
];

// One-time announcement that the new management frontend exists. Uses the same
// per-user persistence as the theme introduction (hasBeenIntroducedTo) and is
// shown only after that introduction is done, so dialogs never stack.
export default function NewFrontendIntroductionModal() {
  const theme = useTheme();
  const location = useLocation();
  const auth = useAuth();
  const { acknowledgeAnnouncement, trackSwitchToNewFrontend } = useFrontendUsage();
  const [saving, setSaving] = useState(false);
  const [dismissVisible, setDismissVisible] = useState(false);

  const shouldShow = useMemo(
    () =>
      Boolean(
        auth.token &&
          auth.userPreferencesHydrated &&
          !auth.isCounterDevice &&
          !PUBLIC_PATHS.has(location.pathname) &&
          Array.isArray(auth.hasBeenIntroducedTo) &&
          auth.hasBeenIntroducedTo.includes('themeColorMode') &&
          !auth.hasBeenIntroducedTo.includes(INTRODUCTION_KEY)
      ),
    [
      auth.token,
      auth.userPreferencesHydrated,
      auth.isCounterDevice,
      auth.hasBeenIntroducedTo,
      location.pathname,
    ]
  );

  useEffect(() => {
    if (!shouldShow) {
      setDismissVisible(false);
      return undefined;
    }
    const timer = setTimeout(() => setDismissVisible(true), DISMISS_REVEAL_MS);
    return () => clearTimeout(timer);
  }, [shouldShow]);

  const markSeenLocally = () => {
    if (auth.setHasBeenIntroducedTo) {
      auth.setHasBeenIntroducedTo(prev => [...new Set([...(prev || []), INTRODUCTION_KEY])]);
    }
  };

  const handleDismiss = async () => {
    setSaving(true);
    try {
      await acknowledgeAnnouncement('dismissed');
      markSeenLocally();
    } catch (_e) {
      // Toast comes from the hook; keep the dialog open so the user can retry.
    } finally {
      setSaving(false);
    }
  };

  const handleTryNow = async () => {
    setSaving(true);
    try {
      await acknowledgeAnnouncement('tryNow');
      markSeenLocally();
      const viewTitle = resolveViewTitle({
        pathname: location.pathname,
        menuItems: auth.menuItems,
      });
      try {
        await trackSwitchToNewFrontend({
          path: location.pathname,
          viewTitle: viewTitle || undefined,
          origin: 'announcement-dialog',
        });
      } catch (_e) {
        // Tracking must never block the switch.
      }
      window.location.assign(buildNewFrontendUrl(location.pathname));
    } catch (_e) {
      setSaving(false);
    }
  };

  if (!shouldShow) return null;

  return (
    <Dialog
      open={shouldShow}
      onClose={() => {}}
      fullWidth
      maxWidth='sm'
      disableEscapeKeyDown
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            backgroundColor: alpha('#000000', 0.52),
          },
        },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
          backgroundImage: t =>
            t.palette.mode === 'dark'
              ? `linear-gradient(145deg, ${alpha(theme.palette.primary.dark, 0.35)} 0%, ${theme.palette.background.paper} 42%)`
              : `linear-gradient(145deg, ${alpha(theme.palette.primary.light, 0.45)} 0%, ${theme.palette.background.paper} 40%)`,
        },
      }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              bgcolor: alpha(
                theme.palette.primary.main,
                theme.palette.mode === 'dark' ? 0.22 : 0.35
              ),
            }}
          >
            <AutoAwesomeIcon sx={{ color: 'text.primary' }} />
          </Box>
          <Typography variant='h5' component='h1' sx={{ fontWeight: 700 }}>
            Das neue FunZone Management ist da!
          </Typography>
        </Stack>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1.5, maxWidth: 500 }}>
          Wir bauen das Management-Tool von Grund auf neu – schneller, aufgeräumter und fit für
          alles, was kommt. Es ist fertig für den Alltag: Wechsle jetzt, du landest genau auf der
          Ansicht, auf der du gerade bist.
        </Typography>
      </Box>
      <DialogContent sx={{ pt: 1.5, pb: 2 }}>
        <Stack spacing={1.5}>
          {HIGHLIGHTS.map(({ Icon, title, description }) => (
            <Stack key={title} direction='row' spacing={1.5} alignItems='flex-start'>
              <Icon sx={{ mt: 0.25, fontSize: 22, color: 'text.secondary' }} />
              <Box>
                <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                  {title}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {description}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{ px: 3, pb: 3, pt: 0, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}
      >
        <Button
          variant='contained'
          color='primary'
          size='large'
          onClick={handleTryNow}
          disabled={saving}
          startIcon={<AutoAwesomeIcon />}
          sx={{ width: '100%', px: 3, py: 1.25, fontWeight: 700, borderRadius: 2 }}
        >
          {saving ? 'Einen Moment…' : 'Zum neuen Management wechseln'}
        </Button>
        {/* The way out stays available, but only after the offer had a moment to
            land — and never as a button competing with the switch. */}
        <Box sx={{ minHeight: 32, display: 'flex', justifyContent: 'center' }}>
          {dismissVisible && (
            <Button
              color='inherit'
              size='small'
              onClick={handleDismiss}
              disabled={saving}
              sx={{
                textTransform: 'none',
                fontWeight: 400,
                color: 'text.disabled',
                '&:hover': { color: 'text.secondary', backgroundColor: 'transparent' },
              }}
            >
              Später – ich bleibe erstmal hier
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
